import { useState, type ReactNode } from 'react';
import { Animated, Box, Packet, Steady, Tag, Wire, beatsOf, useSequence, type Phase } from './Sequence';

/** Lowercase the first letter only, so names like MCP keep their case mid-sentence. */
const lower1 = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/**
 * Figures for the defence half of the launch post (slug grant-the-tool-not-the-server). They play
 * on their own (see Sequence.tsx).
 *
 * Every mechanism shown is on /security and was checked against the code for
 * that page. Messages the product really emits are quoted exactly ("Request
 * blocked by security policy", the 403 text). Where the figure needs a line the
 * product does not have a fixed string for, it is written as plain description,
 * not dressed up as product output.
 */

// ─── 6. One request, every check ────────────────────────────────────────────

interface Step {
  id: string;
  title: string;
  body: string;
  vault?: boolean;
  /** A way to make this step refuse the call, and what the caller then sees. */
  breakIt?: { label: string; result: string; continues?: boolean };
}

const PATH: Step[] = [
  {
    id: 'call',
    title: 'The agent calls a tool',
    body: 'triage-bot sends tools/call for list_issues to its one Statio endpoint.',
  },
  {
    id: 'who',
    title: 'Who is calling?',
    body: 'A short-lived token, five minutes for agents, signed RS256 and checked against published keys.',
    breakIt: {
      label: 'Send an expired token',
      result:
        'Refused before anything else runs: the token is not valid. The agent has to get a new one, and a suspended agent cannot.',
    },
  },
  {
    id: 'org',
    title: 'Which organization?',
    body: 'The call has to belong to the organization that owns the server. Tenancy first, then authority.',
  },
  {
    id: 'device',
    title: 'Device proof, for people',
    body: 'A person’s token is bound to their laptop. Each call carries a proof signed by the device key, valid 60 seconds and usable once.',
    breakIt: {
      label: 'Replay an old proof',
      result: 'Refused: a proof is usable once. A token and proof copied off the machine do not get a second call.',
    },
  },
  {
    id: 'access',
    title: 'Server access, then tool access',
    body: 'The grant on the server covers its tools; a deny on one tool overrides it. Nothing is granted by default, admins included.',
    breakIt: {
      label: 'Call merge_pull_request',
      result:
        "403 · You don't have permission to call tool 'merge_pull_request'. No credential was fetched and GitHub never saw the request.",
    },
  },
  {
    id: 'args',
    title: 'Arguments scanned',
    body: 'Shell and SQL injection, path traversal, requests aimed at internal addresses, oversized inputs. On by default; an organization can switch it to monitor, not off.',
    breakIt: {
      label: 'Pass ../../etc/passwd',
      result: 'Request blocked by security policy. The API behind the server is never touched.',
    },
  },
  {
    id: 'vault',
    title: 'The credential is added',
    body: 'The gateway fetches the GitHub token from encrypted storage (AES-256-GCM) and adds it to the outgoing request. The caller’s own auth headers are stripped first.',
    vault: true,
  },
  {
    id: 'upstream',
    title: 'The server calls GitHub',
    body: 'A hosted server has no direct route out. Its traffic goes through the egress proxy, which checks the destination against that server’s allowlist.',
  },
  {
    id: 'resp',
    title: 'Response checked',
    body: 'Injected instructions are flagged and secrets that appear in a response are masked before the agent sees them.',
    breakIt: {
      label: 'Return a secret in the response',
      result:
        'The call completes, but the key in the response reaches the agent as ghp_•••• . The agent gets the data, not the secret.',
      continues: true,
    },
  },
  {
    id: 'log',
    title: 'Activity records it',
    body: 'Who called which tool on which server, when, how long it took and the status code. Refused calls are recorded too.',
  },
];

const SHORT = ['call', 'token', 'org', 'device', 'access', 'args', 'vault', 'egress', 'response', 'log'];
const LOG = PATH.length - 1;
const SCENARIOS = [
  { id: 'ok', label: 'A normal call' },
  ...PATH.filter((s) => s.breakIt).map((s) => ({ id: s.id, label: s.breakIt!.label })),
];

/** Which steps a scenario visits, and where (if anywhere) it breaks. */
function routeFor(scenario: string) {
  const k = PATH.findIndex((s) => s.id === scenario);
  const brk = k >= 0 ? PATH[k] : null;
  const halts = !!brk && !brk.breakIt?.continues;
  const route = halts ? [...PATH.keys()].filter((i) => i <= k || i === LOG) : [...PATH.keys()];
  return { k, brk, halts, route };
}

/** The narration for step `cur` of a scenario. */
function pathSay(scenario: string, cur: number): ReactNode {
  const { k, brk } = routeFor(scenario);
  const step = PATH[cur];
  if (cur === LOG) {
    if (!brk) return <>All ten checks passed. The agent gets its issues, and the call is on the record.</>;
    return brk.breakIt?.continues ? (
      <>The call completes with the secret masked, and the call is recorded like any other.</>
    ) : (
      <>
        The call stopped at <b>{lower1(brk.title)}</b>. The refusal is recorded like any other call.
      </>
    );
  }
  if (brk && cur === k)
    return (
      <>
        <b>{brk.breakIt?.continues ? 'Caught here, call continues.' : 'Refused here.'}</b> {brk.breakIt?.result}
      </>
    );
  return (
    <>
      <b>
        {step.title}
        {/[?.]$/.test(step.title) ? '' : '.'}
      </b>{' '}
      {step.body}
    </>
  );
}

/** The right-hand panel for step `cur` of a scenario, on a given beat. */
function pathDetail(scenario: string, cur: number, beat: number): ReactNode {
  const { k, brk } = routeFor(scenario);
  const step = PATH[cur];
  return (
    <>
      <span className="k">
        Step {cur + 1} of {PATH.length}
        {step.vault ? ' · held in the vault' : ''}
      </span>
      <h4>{step.title}</h4>
      <p>{step.body}</p>
      {brk && cur === k && beat >= 1 && (
        <div className={`res ${brk.breakIt?.continues ? 'ok' : 'no'}`}>{brk.breakIt?.result}</div>
      )}
      {cur === LOG && (
        <div className={`res ${brk && !brk.breakIt?.continues ? 'no' : 'ok'}`}>
          {brk ? brk.breakIt?.result : 'allowed · list_issues on github · 200'}
        </div>
      )}
    </>
  );
}

/** Every panel RequestPath can show, so the panel never changes size. */
const DETAIL_SIZERS = SCENARIOS.flatMap((sc) => routeFor(sc.id).route.map((cur) => pathDetail(sc.id, cur, 1)));

/** Every line RequestPath can show across all scenarios, so its height never changes. */
const PATH_SIZERS = SCENARIOS.flatMap((sc) => routeFor(sc.id).route.map((cur) => pathSay(sc.id, cur)));

/** One pass of the request through the gateway, for one scenario. */
function PathRun({ scenario, onLoop }: { scenario: string; onLoop: () => void }) {
  const { k, halts } = routeFor(scenario);
  const phases: Phase[] = PATH.map((_, i) => ({ label: SHORT[i], beats: 2, skip: halts && i > k && i < LOG }));
  const seq = useSequence(phases, { beatMs: 900, onLoop });
  const cur = seq.phase;

  const state = (i: number) => {
    if (halts && i === k && cur >= k) return 'halt';
    if (halts && i > k && i < LOG && cur > k) return 'skip';
    if (i === cur) return 'cur';
    if (i < cur) return 'done';
    return '';
  };

  const narrate = (phase: number) => pathSay(scenario, phase);

  return (
    <Animated
      title="One tool call through the gateway"
      seq={seq}
      phases={phases}
      narrate={narrate}
      sizers={PATH_SIZERS}
      beatMs={900}
    >
      <div className="bq-cols">
        <ol className="bq-steps">
          {PATH.map((s, i) => (
            <li
              key={s.id}
              className={[state(i), s.vault ? 'vault' : ''].filter(Boolean).join(' ')}
              aria-current={i === cur ? 'step' : undefined}
            >
              <span className="n" aria-hidden="true">
                {i + 1}
              </span>
              <span>{s.title}</span>
              {state(i) === 'done' && (
                <span className="pw-tag pw-ok" style={{ fontSize: 10 }}>
                  pass
                </span>
              )}
              {state(i) === 'halt' && (
                <span className="pw-tag pw-no" style={{ fontSize: 10 }}>
                  refused
                </span>
              )}
            </li>
          ))}
        </ol>
        <Steady block className="bq-detail" all={DETAIL_SIZERS}>
          {pathDetail(scenario, cur, seq.beat)}
        </Steady>
      </div>
    </Animated>
  );
}

export function RequestPath() {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="bq-scen" role="group" aria-label="Scenario">
        {SCENARIOS.map((s, n) => (
          <button key={s.id} type="button" aria-pressed={n === i} onClick={() => setI(n)}>
            {s.label}
          </button>
        ))}
      </div>
      <PathRun
        key={SCENARIOS[i].id}
        scenario={SCENARIOS[i].id}
        onLoop={() => setI((n) => (n + 1) % SCENARIOS.length)}
      />
    </div>
  );
}

// ─── 7. Where does the attacker land? ───────────────────────────────────────

const LAYERS = [
  { id: 'identity', name: 'Identity and device' },
  { id: 'tools', name: 'Per-tool access' },
  { id: 'scan', name: 'Call scanning' },
  { id: 'vault', name: 'Credential vault' },
  { id: 'egress', name: 'Egress proxy' },
  { id: 'audit', name: 'Activity and audit' },
] as const;
type LayerId = (typeof LAYERS)[number]['id'];

interface Foothold {
  id: string;
  label: string;
  roles: Partial<Record<LayerId, string>>;
  lands: string;
}

const FOOTHOLDS: Foothold[] = [
  {
    id: 'inject',
    label: 'Injected text in a tool response',
    roles: {
      scan: 'Responses are checked for text posing as instructions before the agent reads them.',
      tools:
        'If the agent is fooled anyway, it can only call the tools it was granted. merge_pull_request is still denied.',
      audit: 'Every call it makes because of the injection is recorded under its own name.',
    },
    lands: 'inside the agent’s existing grants, and no further.',
  },
  {
    id: 'laptop',
    label: 'A stolen laptop',
    roles: {
      identity:
        'The token is bound to the device key and every call needs a fresh, single-use proof. Revoke the device and its sessions end.',
      vault: 'No upstream key is on the disk. The only secret there is one revocable Statio token.',
    },
    lands: 'nowhere you cannot switch off from the dashboard.',
  },
  {
    id: 'token',
    label: 'A leaked agent token',
    roles: {
      identity: 'Agent tokens last five minutes. Suspend the agent and it gets no new ones; nobody else is affected.',
      tools: 'For those minutes it holds exactly that agent’s tools.',
      audit: 'You can see which calls were made with it.',
    },
    lands: 'on one agent’s tools, for minutes.',
  },
  {
    id: 'server',
    label: 'A compromised MCP server',
    roles: {
      tools: 'It still only receives the calls callers were allowed to make.',
      vault: 'It never holds stored keys. A credential is added per call, for that call.',
      egress:
        'It has no direct route out. Every destination goes through the proxy and its allowlist, and blocked attempts are logged with host, port and reason.',
      audit: 'The refused exfiltration attempt is a log line you can read.',
    },
    lands: 'inside its own container, with nowhere to send what it sees.',
  },
];

const AL_PHASES: Phase[] = FOOTHOLDS.map((f) => ({ label: f.label.replace(/^An? /, ''), beats: 3 }));

const NOT_HERE = 'Not the layer doing the work here.';

export function AttackerLanding() {
  const seq = useSequence(AL_PHASES, { beatMs: 1100 });
  const f = FOOTHOLDS[seq.phase];
  const lit = seq.beat >= 1;
  return (
    <Animated
      title="Where does the attacker land?"
      seq={seq}
      phases={AL_PHASES}
      beatMs={1100}
      narrate={(phase, beat) => {
        const at = FOOTHOLDS[phase];
        return beat >= 2 ? (
          <>
            <b>Starting from {lower1(at.label)}, an attacker lands</b> {at.lands}
          </>
        ) : (
          <>
            Starting point: <b>{lower1(at.label)}</b>.
          </>
        );
      }}
    >
      <ul className="bq-layers">
        {LAYERS.map((l, n) => {
          const role = f.roles[l.id];
          const on = lit && !!role;
          return (
            <li key={l.id} className={on ? 'on' : 'off'} style={{ transitionDelay: on ? `${n * 120}ms` : '0ms' }}>
              <b>{l.name}</b>
              <Steady all={[NOT_HERE, ...FOOTHOLDS.map((h) => h.roles[l.id]).filter(Boolean)]}>
                {role ?? NOT_HERE}
              </Steady>
            </li>
          );
        })}
      </ul>
    </Animated>
  );
}

// ─── 8. A compromised server tries to phone home ────────────────────────────

interface Attempt {
  id: string;
  label: string;
  host: string;
  port: number;
  allowed: boolean;
  direct?: boolean;
  reason: string;
}

const ATTEMPTS: Attempt[] = [
  { id: 'github', label: 'call github', host: 'api.github.com', port: 443, allowed: true, reason: 'on the allowlist' },
  {
    id: 'paste',
    label: 'exfiltrate',
    host: 'paste.example.net',
    port: 443,
    allowed: false,
    reason: 'not on the allowlist',
  },
  {
    id: 'meta',
    label: 'metadata',
    host: '169.254.169.254',
    port: 80,
    allowed: false,
    reason: 'not on the allowlist, and not port 443',
  },
  {
    id: 'raw',
    label: 'raw socket',
    host: '203.0.113.9',
    port: 4444,
    allowed: false,
    direct: true,
    reason: 'no direct route; the proxy is the only way out',
  },
];
const EG_PHASES: Phase[] = ATTEMPTS.map((a) => ({ label: a.label, beats: 3 }));
const destY = (r: number) => 30 + r * 66;

export function EgressDemo() {
  const seq = useSequence(EG_PHASES, { beatMs: 900 });
  const a = ATTEMPTS[seq.phase];
  const r = seq.phase;
  const b = seq.beat;
  const at = a.direct
    ? [
        { x: 150, y: 200 },
        { x: 228, y: 200 },
        { x: 228, y: 200 },
      ][b]
    : [{ x: 150, y: 200 }, { x: 440, y: 178 }, a.allowed ? { x: 590, y: destY(r) + 23 } : { x: 440, y: 178 }][b];
  const tone = b < 2 ? 'clay' : a.allowed ? 'ok' : 'no';
  const label = b < 2 ? `${a.host}:${a.port}` : a.allowed ? 'allowed' : 'blocked';

  return (
    <Animated
      title={
        <>
          Hosted server <span className="bw-data">github</span>, compromised
        </>
      }
      seq={seq}
      phases={EG_PHASES}
      beatMs={900}
      narrate={(phase, beat) => {
        const t = ATTEMPTS[phase];
        return (
          <>
            The process tries{' '}
            <b>
              {t.host}:{t.port}
            </b>
            .
            {beat >= 2 && (
              <>
                {' '}
                {t.allowed ? 'Allowed:' : 'Blocked:'} {t.reason}.
                {!t.allowed && ' The attempt is logged with host, port and reason.'}
              </>
            )}
          </>
        );
      }}
    >
      <svg
        viewBox="0 0 900 300"
        role="img"
        aria-label="Animated diagram: a compromised hosted MCP server tries four destinations. Only api.github.com on the allowlist gets through the egress proxy; an exfiltration host and the cloud metadata address are blocked at the proxy, and a raw socket has no route out of the container at all."
      >
        <Box
          x={20}
          y={40}
          w={210}
          h={220}
          kicker="hosted server"
          label="github"
          sub="container, no direct route"
          tone={b >= 2 && !a.allowed && a.direct ? 'no' : 'ink'}
          on
        />
        <Tag x={170} y={58} label="compromised" tone="no" />
        <Box
          x={360}
          y={90}
          w={160}
          h={120}
          kicker="egress proxy"
          label="allowlist"
          sub="api.github.com · 443"
          tone={b >= 2 && !a.direct ? (a.allowed ? 'ok' : 'no') : 'clay'}
          on={b === 1}
        />
        <Wire x1={230} y1={150} x2={360} y2={150} tone={!a.direct && b >= 1 ? 'ink' : 'dim'} />
        {ATTEMPTS.map((d, n) => {
          const hit = n === r && b >= 2 && d.allowed;
          return (
            <g key={d.id}>
              <Wire
                x1={d.direct ? 230 : 520}
                y1={d.direct ? 200 : 150}
                x2={640}
                y2={destY(n) + 23}
                tone={hit ? 'ok' : 'dim'}
                dashed={!d.allowed}
              />
              <g
                className={`bq-box${hit ? ' t-ok on' : n === r && b >= 2 ? ' t-no' : ''}`}
                transform={`translate(640 ${destY(n)})`}
              >
                <rect width="240" height="46" rx="3" />
                <text className="bq-lab" x="12" y="23" dominantBaseline="central" style={{ fontSize: 12 }}>
                  {d.host}:{d.port}
                </text>
              </g>
            </g>
          );
        })}
        <Packet x={at.x} y={at.y} label={label} tone={tone} />
      </svg>
    </Animated>
  );
}

// ─── 9. The release gate ────────────────────────────────────────────────────

const RG_PHASES: Phase[] = [
  { act: 'v15', label: 'build', beats: 2 },
  { act: 'v15', label: 'scan', beats: 2 },
  { act: 'v15', label: 'held', beats: 2 },
  { act: 'v15.1', label: 'fix', beats: 2 },
  { act: 'v15.1', label: 'scan', beats: 2 },
  { act: 'v15.1', label: 'approve', beats: 2 },
  { act: 'v15.1', label: 'live', beats: 2 },
  { act: 'v15.1', label: 'roll back', beats: 2 },
];

const VER_NOTES = ['serving', 'building', 'blocked: critical CVE', 'not built', 'rolled back', 'candidate'];

export function ReleaseGate() {
  const seq = useSequence(RG_PHASES, { beatMs: 1000 });
  const p = seq.phase;
  const done = seq.settled;
  const ver = p < 3 ? 'v15' : 'v15.1';
  const serving = p === 6 ? 'v15.1' : 'v14';

  const st = {
    build: p === 0 || p === 3 ? (done ? 'good' : 'cur') : p > 0 ? 'good' : '',
    scan: p === 1 || p === 4 ? (done ? (p === 1 ? 'bad' : 'good') : 'cur') : p === 2 ? 'bad' : p >= 5 ? 'good' : '',
    promote: p === 2 ? 'hold' : p === 5 ? (done ? 'good' : 'cur') : p >= 6 ? 'good' : '',
  };
  const scanLabel =
    (p === 1 && done) || p === 2 ? 'scan · 1 critical' : (p === 4 && done) || p >= 5 ? 'scan · clean' : 'scan';

  const narrate = (phase: number, beat: number) => {
    const fin = beat === beatsOf(RG_PHASES[phase]) - 1;
    return [
      <>v15 pulls in a dependency with a critical CVE. The image is built.</>,
      <>
        Images are scanned with Grype before they can run.
        {fin && (
          <>
            {' '}
            <b>One critical finding.</b>
          </>
        )}
      </>,
      <>
        <b>v15 cannot be promoted</b>, and v14 keeps serving. Fix the dependency and deploy again.
      </>,
      <>The dependency is bumped and v15.1 is built.</>,
      <>
        Scanned again.
        {fin && (
          <>
            {' '}
            <b>Clean.</b>
          </>
        )}
      </>,
      <>
        Promotion is still a person&apos;s decision, and it needs the approve permission, which deploy rights alone do
        not include.
      </>,
      <>
        <b>v15.1 is live.</b> If it misbehaves, the previous version is one click away.
      </>,
      <>
        Rolled back. <b>v14 is serving again.</b>
      </>,
    ][phase];
  };

  return (
    <Animated
      title={
        <>
          Deploying <span className="bw-data">github</span>
        </>
      }
      seq={seq}
      phases={RG_PHASES}
      narrate={narrate}
      beatMs={1000}
    >
      <div className="bq-pipe">
        <span className={`st ${st.build}`}>
          <Steady all={['build v15', 'build v15.1']}>build {ver}</Steady>
        </span>
        <span className="ar" aria-hidden="true">
          →
        </span>
        <span className={`st ${st.scan}`}>
          <Steady all={['scan', 'scan · 1 critical', 'scan · clean']}>{scanLabel}</Steady>
        </span>
        <span className="ar" aria-hidden="true">
          →
        </span>
        <span className={`st ${st.promote}`}>
          <Steady all={['promote', 'promote · blocked']}>{p === 2 ? 'promote · blocked' : 'promote'}</Steady>
        </span>
        <span className="ar" aria-hidden="true">
          →
        </span>
        <span className="st good">
          <Steady all={['live: v14', 'live: v15.1']}>live: {serving}</Steady>
        </span>
      </div>
      <div className="bq-live">
        {['v14', 'v15', 'v15.1'].map((v) => {
          const cls = v === serving ? 'serving' : v === 'v15' && p >= 2 ? 'out' : v === 'v15.1' && p < 3 ? 'out' : '';
          const note =
            v === serving
              ? 'serving'
              : v === 'v15'
                ? p >= 2
                  ? 'blocked: critical CVE'
                  : 'building'
                : p < 3
                  ? 'not built'
                  : p === 7
                    ? 'rolled back'
                    : 'candidate';
          return (
            <div key={v} className={`bq-ver ${cls}`}>
              {v}
              <small>
                <Steady all={VER_NOTES}>{note}</Steady>
              </small>
            </div>
          );
        })}
      </div>
    </Animated>
  );
}
